import { useEffect, useState } from "react";
import { CornerDownRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { Priority, StageKey, Task } from "../../mock/types";
import { PROFILES, PROJECTS, STAGES, TASK_TYPES } from "../../mock/data";

const selectClass =
	"h-9 w-full rounded-lg border border-border-strong bg-surface px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

const label = "mb-1 block text-xs font-medium text-muted";

type Draft = {
	title: string;
	description: string;
	project_id: string;
	priority: Priority;
	status: StageKey;
	assignee_id: string;
	task_type_id: string;
	due_date: string;
};

function emptyDraft(parentTask: Task | null): Draft {
	return {
		title: "",
		description: "",
		project_id: parentTask?.project_id ?? PROJECTS[0].id,
		priority: "medium",
		status: "todo",
		assignee_id: "",
		task_type_id: TASK_TYPES[0].id,
		due_date: "",
	};
}

function Row({
	icon,
	label,
	children,
}: {
	icon: React.ReactNode;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex items-start gap-3 py-2.5">
			<div className="flex w-24 shrink-0 items-center gap-1.5 text-xs text-muted">
				{icon}
				{label}
			</div>
			<div className="flex-1 text-sm text-foreground">{children}</div>
		</div>
	);
}

export function CreateTaskDrawer({
	parentTask,
	onClose,
	onCreate,
}: {
	parentTask: Task | null;
	onClose: () => void;
	onCreate: (task: Task) => void;
}) {
	const [draft, setDraft] = useState<Draft>(() => emptyDraft(parentTask));

	// Reset the draft each time the drawer opens for a (new) parent context.
	useEffect(() => {
		setDraft(emptyDraft(parentTask));
	}, [parentTask]);

	const set = (patch: Partial<Draft>) =>
		setDraft((d) => ({ ...d, ...patch }));

	const submit = () => {
		if (!draft.title.trim()) return;
		const now = new Date().toISOString();
		const assignee =
			PROFILES.find((p) => p.id === draft.assignee_id) ?? null;
		onCreate({
			id: `TASK-${Math.floor(Math.random() * 100000)}`,
			project_id: draft.project_id,
			ticket_code: null,
			title: draft.title.trim(),
			description: draft.description.trim() || null,
			status: draft.status,
			sub_status: null,
			priority: draft.priority,
			assigned_to: assignee,
			additional_assignees: [],
			created_by: PROFILES[0],
			due_date: draft.due_date || null,
			tags: [],
			estimated_time: 0,
			sprint_id: null,
			parent_task_id: parentTask?.id ?? null,
			task_type_id: draft.task_type_id,
			has_calendar_event: false,
			github: null,
			checklist: [],
			comments: [],
			history: [
				{
					id: `h-${Date.now()}`,
					actor: PROFILES[0],
					summary: parentTask ? "created the subtask" : "created the task",
					created_at: now,
				},
			],
			attachments: [],
			created_at: now,
			updated_at: now,
		});
	};

	return (
		<>
			<header className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
				<div className="flex items-center gap-2">
					<span className="text-sm font-semibold text-foreground">
						{parentTask ? "New Subtask" : "New Task"}
					</span>
					<select
						value={draft.status}
						onChange={(e) =>
							set({ status: e.target.value as StageKey })
						}
						className="h-7 rounded-lg border border-border-strong bg-surface px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
					>
						{STAGES.map((s) => (
							<option key={s.key} value={s.key}>
								{s.name}
							</option>
						))}
					</select>
					<select
						value={draft.priority}
						onChange={(e) =>
							set({ priority: e.target.value as Priority })
						}
						className="h-7 rounded-lg border border-border-strong bg-surface px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
					>
						<option value="low">Low</option>
						<option value="medium">Medium</option>
						<option value="high">High</option>
						<option value="urgent">Urgent</option>
					</select>
				</div>
				<button
					type="button"
					onClick={onClose}
					className="rounded-md p-1.5 text-muted hover:bg-muted-subtle hover:text-foreground"
					aria-label="Close"
				>
					<X className="h-4 w-4" />
				</button>
			</header>

			<div className="grid flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[1fr_300px]">
				<div className="min-w-0 space-y-6 px-5 py-5">
					{parentTask && (
						<span className="inline-flex items-center gap-1 text-xs text-primary">
							<CornerDownRight className="h-3 w-3" />
							Subtask of{" "}
							{parentTask.ticket_code ?? parentTask.title}
						</span>
					)}

					<div>
						<label className={label}>Title</label>
						<Input
							autoFocus
							value={draft.title}
							onChange={(e) => set({ title: e.target.value })}
							placeholder="What needs to be done?"
						/>
					</div>

					<div>
						<label className={label}>Description</label>
						<textarea
							value={draft.description}
							onChange={(e) =>
								set({ description: e.target.value })
							}
							placeholder="Add more detail..."
							rows={5}
							className="w-full rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
						/>
					</div>
				</div>

				<div className="border-t border-border bg-muted-subtle/30 px-5 py-5 md:border-l md:border-t-0">
					<div className="divide-y divide-border">
						{!parentTask && (
							<Row
								icon={
									<span className="h-3.5 w-3.5 rounded-full bg-border-strong" />
								}
								label="Project"
							>
								<select
									value={draft.project_id}
									onChange={(e) =>
										set({ project_id: e.target.value })
									}
									className={selectClass}
								>
									{PROJECTS.map((p) => (
										<option key={p.id} value={p.id}>
											{p.name}
										</option>
									))}
								</select>
							</Row>
						)}

						<Row
							icon={
								<span className="text-[13px] leading-none">
									🏷️
								</span>
							}
							label="Type"
						>
							<select
								value={draft.task_type_id}
								onChange={(e) =>
									set({ task_type_id: e.target.value })
								}
								className={selectClass}
							>
								{TASK_TYPES.map((t) => (
									<option key={t.id} value={t.id}>
										{t.name}
									</option>
								))}
							</select>
						</Row>

						<Row
							icon={
								<span className="text-[13px] leading-none">
									👤
								</span>
							}
							label="Assignee"
						>
							<select
								value={draft.assignee_id}
								onChange={(e) =>
									set({ assignee_id: e.target.value })
								}
								className={selectClass}
							>
								<option value="">Unassigned</option>
								{PROFILES.map((p) => (
									<option key={p.id} value={p.id}>
										{p.full_name ?? p.email}
									</option>
								))}
							</select>
						</Row>

						<Row
							icon={
								<span className="text-[13px] leading-none">
									📅
								</span>
							}
							label="Due date"
						>
							<Input
								type="date"
								value={draft.due_date}
								onChange={(e) =>
									set({ due_date: e.target.value })
								}
							/>
						</Row>
					</div>
				</div>
			</div>

			<footer className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
				<Button variant="outline" onClick={onClose}>
					Cancel
				</Button>
				<Button onClick={submit} disabled={!draft.title.trim()}>
					{parentTask ? "Create Subtask" : "Create Task"}
				</Button>
			</footer>
		</>
	);
}
